// src/hooks/useTextComparison.ts

import { useState, useRef, useEffect, useCallback } from 'react';
import apiService, { ComparisonOptions } from '../services/apiService';
import fileService from '../services/FileService';
import { getFromStorage, saveToStorage } from '../utils/storageUtils';
import { ImpactFlag } from '../components/TextCompare/ComparisonSummary';

export interface AISettings {
    showAiSummary: boolean;
}

export type TransformType = 'lowercase' | 'sort' | 'trim' | 'clean';

export const useTextComparison = () => {
    const DEFAULT_OPTIONS: ComparisonOptions = {
        ignoreCase: false,
        ignoreWhitespace: false,
        ignorePunctuation: false,
        characterLevel: false, // Default to Word precision
        detectMoves: true,
        realTime: false, // Default to Live Sync OFF
        viewType: 'split'
    };

    // Inputs
    const [oldText, setOldText] = useState<string>('');
    const [newText, setNewText] = useState<string>('');
    const [fileName1, setFileName1] = useState<string>('');
    const [fileName2, setFileName2] = useState<string>('');

    // Results
    const [comparison, setComparison] = useState<any[] | null>(null);
    const [matchPercentage, setMatchPercentage] = useState<number>(0);
    const [diffCount, setDiffCount] = useState<number>(0);
    const [stats, setStats] = useState<any>(null);
    const [summary, setSummary] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // View Settings
    const [options, setOptions] = useState<ComparisonOptions>(() =>
        getFromStorage('text-compare-options-v2', DEFAULT_OPTIONS)
    );

    // AI Settings & State
    const [aiSettings, setAiSettings] = useState<AISettings>({
        showAiSummary: false
    });
    const [aiSummary, setAiSummary] = useState<string>('');
    const [impactFlags, setImpactFlags] = useState<ImpactFlag[]>([]);
    const [isAiLoading, setIsAiLoading] = useState<boolean>(false);

    // Transformation State
    const [activeTransforms, setActiveTransforms] = useState<Set<TransformType>>(new Set());
    const baseContentRef = useRef<{ old: string; new: string }>({ old: '', new: '' });

    // Auto-save options
    useEffect(() => {
        saveToStorage('text-compare-options-v2', options);
    }, [options]);

    // Auto-save AI settings
    useEffect(() => {
        saveToStorage('text-compare-ai-settings', aiSettings);
    }, [aiSettings]);

    // Clear AI summary when text changes
    useEffect(() => {
        setAiSummary('');
        setImpactFlags([]);
    }, [oldText, newText]);

    const handleCompare = useCallback(async (overrideOld?: string, overrideNew?: string) => {
        const text1 = overrideOld !== undefined ? overrideOld : oldText;
        const text2 = overrideNew !== undefined ? overrideNew : newText;

        if (!text1.trim() && !text2.trim()) return;

        setIsLoading(true);
        setError(null);
        // Reset AI state for the new comparison
        setAiSummary('');
        setImpactFlags([]);
        setAiSettings(prev => ({ ...prev, showAiSummary: false }));

        try {
            const result = await apiService.compareTexts(text1, text2, 'patience', options);

            setComparison(result.comparison || null);
            setMatchPercentage(result.matchPercentage);
            setDiffCount(result.diffCount);
            setStats(result.overallStats || null);
            setSummary(result.changeSummary || '');
        } catch (err: any) {
            console.error("Comparison error:", err);
            setError(err.message || "An unexpected error occurred.");
        } finally {
            setIsLoading(false);
        }
    }, [oldText, newText, options]);

    const handleGenerateAiSummary = useCallback(async () => {
        const t1 = oldText || '';
        const t2 = newText || '';
        if (!t1.trim() || !t2.trim()) return;

        setIsAiLoading(true);
        try {
            const aiOptions = { ...options, deepAnalysis: true };
            const result = await apiService.generateSummary(oldText, newText, 'patience', aiOptions);
            setAiSummary(result.changeSummary);
            setImpactFlags(result.impactFlags || []);
        } catch (err) {
            console.error("AI Summary error:", err);
            setAiSummary("Unable to generate AI summary at this time.");
            setImpactFlags([]);
        } finally {
            setIsAiLoading(false);
        }
    }, [oldText, newText, options]);

    // Transformation Logic
    const applyTransforms = (text: string, transforms: Set<TransformType>): string => {
        if (!text) return '';
        let result = text;
        let currentLines = text.split('\n');

        if (transforms.has('clean')) currentLines = currentLines.filter(l => l.trim().length > 0);
        if (transforms.has('trim')) currentLines = currentLines.map(l => l.trim());

        result = currentLines.join('\n');

        if (transforms.has('sort')) {
            result = result.split('\n').sort((a, b) => a.localeCompare(b)).join('\n');
        }
        if (transforms.has('lowercase')) {
            result = result.toLowerCase();
        }
        return result;
    };

    const handleTransformText = useCallback((type: TransformType) => {
        const newTransforms = new Set(activeTransforms);

        // Toggle Logic for Switchable Transforms
        if (newTransforms.has(type)) {
            newTransforms.delete(type);
        } else {
            if (newTransforms.size === 0) {
                baseContentRef.current = { old: oldText, new: newText };
            }
            newTransforms.add(type);
        }

        setActiveTransforms(newTransforms);

        if (newTransforms.size === 0) {
            // Revert
            setOldText(baseContentRef.current.old);
            setNewText(baseContentRef.current.new);
            handleCompare(baseContentRef.current.old, baseContentRef.current.new);
        } else {
            // Apply pipeline
            const t1 = applyTransforms(baseContentRef.current.old, newTransforms);
            const t2 = applyTransforms(baseContentRef.current.new, newTransforms);
            setOldText(t1);
            setNewText(t2);
            handleCompare(t1, t2);
        }
    }, [activeTransforms, oldText, newText, handleCompare]);

    // Wrappers to handle manual text edits (clears active toggles)
    const handleOldTextChange = (val: string) => {
        if (activeTransforms.size > 0) setActiveTransforms(new Set());
        setOldText(val);
    };

    const handleNewTextChange = (val: string) => {
        if (activeTransforms.size > 0) setActiveTransforms(new Set());
        setNewText(val);
    };

    const updateOptions = (newOpts: Partial<ComparisonOptions>) => {
        setOptions(prev => ({ ...prev, ...newOpts }));
    };

    const handleClear = () => {
        setOldText('');
        setNewText('');
        setFileName1('');
        setFileName2('');
        setComparison(null);
        setMatchPercentage(0);
        setDiffCount(0);
        setStats(null);
        setSummary('');
        setAiSummary('');
        setImpactFlags([]);
        setError(null);
        setOptions(DEFAULT_OPTIONS);
    };

    const handleFileUpload = async (file: File, side: 1 | 2) => {
        try {
            const text = await fileService.extractText(file);
            if (side === 1) {
                setOldText(text);
                setFileName1(file.name);
            } else {
                setNewText(text);
                setFileName2(file.name);
            }
        } catch (err: any) {
            setError(`Failed to extract text from ${file.name}: ${err.message}`);
        }
    };

    const setAiShowSummary = (show: boolean) => {
        setAiSettings(prev => ({ ...prev, showAiSummary: show }));
    };

    return {
        // State
        oldText,
        newText,
        fileName1,
        fileName2,
        comparison,
        matchPercentage,
        diffCount,
        stats,
        summary,
        isLoading,
        error,
        options,
        aiSettings,
        aiSummary,
        impactFlags,
        isAiLoading,
        activeTransforms,

        // Actions
        handleOldTextChange,
        handleNewTextChange,
        handleCompare,
        handleGenerateAiSummary,
        handleTransformText,
        updateOptions,
        handleClear,
        handleFileUpload,
        setAiShowSummary,
        setFileName1,
        setFileName2
    };
};

export default useTextComparison;
